import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';

// ---------------------------------------------------------------------------
// StudentInsightList — vertical list of students, ranked by signal.
//
// Every student on the caseload appears here so nobody gets "hidden by
// the algorithm." The critical + attention groups are sorted to the top;
// everyone else appears in signal-strength order under them.
//
// Clicking a student surfaces the StudentInsightPanel for that student,
// which renders the deterministic breakdown plus the on-demand AI
// narrative controls.
// ---------------------------------------------------------------------------

const SIGNAL_ORDER = { critical: 5, attention: 4, strong: 3, steady: 2, quiet: 1 };

const SIGNAL_META = {
  critical: { label: 'Critical', dot: 'bg-red-500', text: 'text-red-700', chip: 'bg-red-50 text-red-700' },
  attention: { label: 'Attention', dot: 'bg-amber-500', text: 'text-amber-700', chip: 'bg-amber-50 text-amber-700' },
  strong: { label: 'Strong', dot: 'bg-emerald-500', text: 'text-emerald-700', chip: 'bg-emerald-50 text-emerald-700' },
  steady: { label: 'Steady', dot: 'bg-slate-400', text: 'text-slate-600', chip: 'bg-slate-100 text-slate-700' },
  quiet: { label: 'Quiet', dot: 'bg-slate-300', text: 'text-slate-500', chip: 'bg-slate-100 text-slate-500' },
};

const FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'critical', label: 'Critical' },
  { id: 'attention', label: 'Attention' },
  { id: 'strong', label: 'Strong' },
  { id: 'quiet', label: 'Quiet' },
];

const StudentInsightList = ({ studentInsights = [], selectedId, onSelect }) => {
  const [filter, setFilter] = useState('all');

  const sorted = useMemo(() => {
    const filtered = filter === 'all'
      ? studentInsights
      : studentInsights.filter((s) => s.signal === filter);
    return [...filtered].sort((a, b) => {
      const sa = SIGNAL_ORDER[a.signal] || 0;
      const sb = SIGNAL_ORDER[b.signal] || 0;
      if (sb !== sa) return sb - sa;
      return (a.studentName || '').localeCompare(b.studentName || '');
    });
  }, [studentInsights, filter]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col">
      <div className="px-4 pt-4 pb-2 border-b border-slate-100">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Users" size={18} className="text-slate-500" />
          <h3 className="font-semibold text-slate-900">Students</h3>
          <span className="text-xs text-slate-500 ml-auto">
            {studentInsights.length} on caseload
          </span>
        </div>
        <div className="flex gap-1 -mx-1 overflow-x-auto">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setFilter(tab.id)}
              className={`px-2.5 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                filter === tab.id
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <ul className="max-h-[540px] overflow-y-auto divide-y divide-slate-100">
        {sorted.length === 0 && (
          <li className="p-6 text-sm text-slate-500 text-center">
            No students in this bucket.
          </li>
        )}
        {sorted.map((s) => {
          const meta = SIGNAL_META[s.signal] || SIGNAL_META.quiet;
          const isSelected = selectedId === s.studentId;
          return (
            <li key={s.studentId}>
              <button
                type="button"
                onClick={() => onSelect && onSelect(s.studentId)}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${
                  isSelected ? 'bg-slate-50' : 'hover:bg-slate-50'
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full mt-1.5 ${meta.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-900 truncate">
                      {s.studentName}
                    </span>
                    <span className={`text-xs font-medium ${meta.text}`}>
                      {meta.label}
                    </span>
                  </div>
                  {s.highlights && s.highlights.length > 0 ? (
                    <p className="text-xs text-slate-600 truncate mt-0.5">
                      {s.highlights[0]}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-400 italic mt-0.5">
                      No highlights yet.
                    </p>
                  )}
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {s.activeGoalCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {s.activeGoalCount} active goal{s.activeGoalCount === 1 ? '' : 's'}
                      </span>
                    )}
                    {s.incidentCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {s.incidentCount} incident{s.incidentCount === 1 ? '' : 's'}
                      </span>
                    )}
                    {s.review?.overdue && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                        review overdue
                      </span>
                    )}
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default StudentInsightList;
